<template>
	<div>
		<PageHeader
			:data="{
				name: route,
				breadcrumbs: [{ name: 'Home', to: '/' }],
			}"
		/>

		<div v-if="!error" class="row mb-3">
			<!-- Filters -->
			<div class="col-12 mb-2">
				<div class="card">
					<div class="card-body">
						<div class="d-flex justify-content-between align-items-center">
							<div class="header d-flex align-items-center">
								<h4 class="p-0 m-0 me-2"><font-awesome-icon :icon="['fas', 'filter']" class="text-primary me-1" /> Filters</h4>
								<button v-if="Object.values(this.params).length > 4 && searchActive === ''" @click="clearFilters()" class="btn btn-sm btn-danger text-white me-2">
									Clear
									<font-awesome-icon :icon="['fas', 'times']" />
								</button>
								<button v-if="!searchSaving && haveParamsToSave() && searchActive === ''" @click="searchSaving = true" class="btn btn-sm btn-primary">
									Save search
								</button>
								<div v-if="searchSaving && searchActive === ''" class="save-search d-flex">
									<form @submit.prevent="storeSearch" class="d-flex">
										<input type="text" v-model="searchName" class="form-control form-control-sm ms-2" placeholder="enter search name" required />
										<button type="submit" class="btn btn-sm btn-primary ms-1">
											Save
										</button>
										<a role="button" @click="searchSaving = false" class="btn btn-sm ms-1">
											Cancel
										</a>
									</form>
								</div>
								<span v-if="searchActive !== ''" class="badge bg-success"
									>{{ searchActive }} <font-awesome-icon role="button" @click="clearFilters()" class="ms-2" :icon="['fas', 'times']"
								/></span>
							</div>
							<div class="action-buttons">
								<button
									data-bs-toggle="offcanvas"
									data-bs-target="#offcanvasSearches"
									aria-controls="offcanvasSearches"
									class="btn btn-outline-secondary btn-sm me-2"
								>
									Saved Searches <font-awesome-icon :icon="['fas', 'save']" class="me-1" />
								</button>
							</div>
						</div>

						<LPDataTableFilters :filters="table.filters" v-on:getFilters="getFilters($event.filters)" />
					</div>
				</div>
			</div>
			<!-- Table data -->
			<div class="col-12 mb-2">
				<div class="card">
					<div class="card-body">
						<div class="d-flex justify-content-between align-items-center">
							<div class="header d-flex align-items-center">
								<h4 class="p-0 m-0 me-2 text-capitalize">{{ model }}</h4>
								<button
									v-if="authUser.permissions.includes('CAMPAIGN_CREATE')"
									data-bs-toggle="modal"
									data-bs-target="#campaignModal"
									class="btn btn-primary btn-sm me-2"
								>
									Custom campaign <font-awesome-icon :icon="['fas', 'plus-circle']" class="me-1" />
								</button>
							</div>

							<div class="action-buttons">
								<button
									data-bs-toggle="offcanvas"
									data-bs-target="#offcanvasTableSettings"
									aria-controls="offcanvasTableSettings"
									class="btn btn-outline-secondary btn-sm"
								>
									Table Settings <font-awesome-icon :icon="['fas', 'cogs']" class="me-1" />
								</button>
							</div>
						</div>
						<table :class="!loading && tableData.length > 0 ? 'table table-hover' : 'table table-hover loading'">
							<LPDataTableThead :thead="table.columns" :userColumns="userColumns" v-on:setOrder="setOrderBy($event.orderBy, $event.direction)" />
							<LPDataTableTbody
								:userColumns="userColumns"
								:authUser="authUser"
								:data="tableData"
								:tbody="table.columns"
								:model="model"
								:route="route"
								v-on:deleted="getTableData"
								:rowInfo="true"
								v-on:showInformationModal="showInformationModal($event)"
							/>
						</table>
						<div v-if="!loading && tableData.length === 0" class="col-md-12 text-center">
							<h4>No data available</h4>
						</div>
						<div v-if="loading && tableData.length === 0" class="d-flex justify-content-center">
							<div class="spinner-border" role="status">
								<span class="visually-hidden">Loading...</span>
							</div>
						</div>
						<div class="table-actions d-flex" v-if="false">
							<button data-bs-toggle="modal" data-bs-target="#exportModal" class="btn btn-success btn-sm me-2">
								Export <font-awesome-icon :icon="['fas', 'file-csv']" class="me-1" />
							</button>
						</div>
						<LPDataTablePagination :pagination="pagination" v-on:getItems="getItems($event.page)" />
					</div>
				</div>
			</div>
		</div>

		<div v-else class="row mb-3">
			<div class="col-12">
				<div class="card">
					<div class="card-body">
						<div class="alert alert-danger" role="alert">
							{{ error }}
						</div>
					</div>
				</div>
			</div>
		</div>

		<!-- Modal for adding Data -->
		<div class="modal fade" id="create" tabindex="-1" aria-labelledby="createLabel" aria-hidden="true">
			<div class="modal-dialog">
				<div class="modal-content">
					<form @submit.prevent="create()">
						<div class="modal-header">
							<h5 class="modal-title" id="exampleModalLabel">New user</h5>
							<small class="ms-2">User default role is user</small>
							<button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
						</div>
						<div class="modal-body">
							<h1>Create {{ model }}</h1>
						</div>
						<div class="modal-footer">
							<button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
							<button type="submit" class="btn btn-primary">Save</button>
						</div>
					</form>
				</div>
			</div>
		</div>

		<!-- Modal for creating campaign -->
		<div class="modal fade" id="campaignModal" tabindex="-1" aria-labelledby="campaignModalLabel" aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="false">
			<div class="modal-dialog modal-lg">
				<div class="modal-content">
					<div class="modal-header">
						<div class="d-flex justify-content-start align-items-center">
							<h5 class="modal-title me-2">Create Custom Campaign</h5>
							<a href="javascript:void(0)" v-if="customCampaign.typeConfirmed" @click="customCampaign.typeConfirmed = false" class="btn btn-primary btn-sm">Back</a>
						</div>

						<button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
					</div>
					<div class="modal-body mb-0">
						<!-- First step chose method for importing -->
						<div v-if="!customCampaign.typeConfirmed" class="">
							<h5 class="mb-3">Choose which method you want to use to create a custom campaign ❓</h5>

							<div class="form-check">
								<input class="form-check-input" type="radio" v-model="customCampaign.type" value="manual" id="customCampaignTypeManual" />
								<label class="form-check-label" for="customCampaignTypeManual">
									Select municipalities one by one with search functionality
								</label>
							</div>
							<div v-if="customCampaign.type === 'manual'" class="p-2 border mb-2 rounded-1 ms-3">
								<small>You will be provided with search field to find and select municipalities that you want to add in custom campaign.</small>
							</div>

							<div class="form-check">
								<input class="form-check-input" type="radio" v-model="customCampaign.type" value="import" id="customCampaignTypeImport" />
								<label class="form-check-label" for="customCampaignTypeImport">
									Import municipalities list
								</label>
							</div>
							<div v-if="customCampaign.type === 'import'" class="p-2 border mb-2 rounded-1 ms-4">
								<small>You will be provided with import functionality with some options</small>
							</div>
						</div>
						<!-- If selected type of import then go here -->
						<div v-else>
							<!-- Manual choosing municipalities -->
							<div v-if="customCampaign.typeConfirmed && !customCampaign.dataConfirmed && customCampaign.type === 'manual'">
								<h5>Add municipalities for custom campaign</h5>

								<!-- Select municipality if missing for type municipality -->
								<MunicipalityAdvancedSearch class="mb-2" v-on:setMunicipality="addMunicipalityToCampaign" />

								<table class="table" v-if="customCampaign.municipalities.length > 0">
									<thead>
										<tr>
											<th>Name</th>
											<th>County</th>
											<th>State</th>
											<th>Population</th>
											<th width="30"></th>
										</tr>
									</thead>
									<tbody>
										<tr :key="municipality.id" v-for="municipality in customCampaign.municipalities">
											<td>{{ municipality.full_name }}</td>
											<td>{{ municipality.county }}</td>
											<td>{{ municipality.state }}</td>
											<td>{{ municipality.population }}</td>
											<th width="30">
												<a href="javascript:void(0)" @click="removeMunicipalityFromCampaign(municipality.id)" class="btn btn-sm btn-danger text-white">
													<font-awesome-icon :icon="['fas', 'times']" />
												</a>
											</th>
										</tr>
									</tbody>
								</table>

								<button
									type="button"
									class="btn btn-primary float-end"
									v-if="customCampaign.municipalitiesIds.length > 0"
									@click="customCampaign.dataConfirmed = true"
								>
									Confirm municipalities
								</button>
							</div>
							<!-- Importing custom municipalities -->
							<div v-if="customCampaign.typeConfirmed && !customCampaign.dataConfirmed && customCampaign.type === 'import'">
								<label class="form-label">Upload CSV with municipality IDs</label>
								<input
									type="file"
									class="form-control form-control-sm"
									accept=".csv,text/csv"
									@change="onImportFileChange"
								/>
								<div class="form-text">Accepted columns: `municipality_id` or `id` (first column also works).</div>
								<button
									type="button"
									class="btn btn-outline-primary btn-sm mt-2"
									@click="uploadMunicipalitiesCsv"
									:disabled="customCampaign.importLoading || !customCampaign.importFile"
								>
									<span v-if="customCampaign.importLoading" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
									Upload list
								</button>
								<div v-if="customCampaign.importError" class="alert alert-danger mt-2 mb-0">
									{{ customCampaign.importError }}
								</div>
								<div v-if="customCampaign.importSummary" class="alert alert-info mt-2 mb-0">
									Found {{ customCampaign.importSummary.found_ids }} municipalities
									<span v-if="customCampaign.importSummary.missing_ids > 0">, {{ customCampaign.importSummary.missing_ids }} missing</span>
									<span v-if="customCampaign.importSummary.invalid_rows > 0">, {{ customCampaign.importSummary.invalid_rows }} invalid rows</span>.
								</div>

								<table class="table mt-2" v-if="customCampaign.municipalities.length > 0">
									<thead>
										<tr>
											<th>Name</th>
											<th>County</th>
											<th>State</th>
											<th>Population</th>
										</tr>
									</thead>
									<tbody>
										<tr :key="municipality.id" v-for="municipality in customCampaign.municipalities">
											<td>{{ municipality.full_name }}</td>
											<td>{{ municipality.county }}</td>
											<td>{{ municipality.state }}</td>
											<td>{{ municipality.population }}</td>
										</tr>
									</tbody>
								</table>

								<button
									type="button"
									v-if="customCampaign.municipalitiesIds.length > 0"
									class="btn btn-primary float-end"
									@click="customCampaign.dataConfirmed = true"
								>
									Confirm municipalities
								</button>
							</div>
						</div>

						<!-- Last step Campaign data -->
						<div v-if="customCampaign.dataConfirmed" class="campaign-info">
							<div class="form-group mb-2">
								<label for="">Campaign Name</label>
								<div class="input-group mb-3">
									<input type="text" class="form-control" placeholder="Campaign name" v-model="customCampaign.name" required />
								</div>
							</div>
							<div class="form-group mb-2">
								<label for="">Description</label>
								<div class="input-group mb-3">
									<textarea class="form-control" placeholder="Campaign description" v-model="customCampaign.description" />
								</div>
							</div>
							<div class="form-group mb-2">
								<label for="">UTM Campaign</label>
								<div class="input-group mb-3">
									<utm-campaign-input v-model="customCampaign.utm_campaign" />
								</div>
							</div>
							<div class="form-group mb-2">
								<label>UTM Content</label>
								<utm-content-input v-model="customCampaign.utm_content" />
							</div>
							<div>
								<span class="badge badge-primary bg-success w-100 py-3"
									><h4 class="m-0 p-0">{{ customCampaign.municipalities.length }} municipalities</h4></span
								>
							</div>
						</div>
					</div>
					<div class="modal-footer p-0 px-1 py-1">
						<button type="button" class="btn btn-default" data-bs-dismiss="modal" aria-label="Close">
							Cancel
						</button>
						<button type="button" v-if="!customCampaign.typeConfirmed" class="btn btn-primary float-end" @click="customCampaign.typeConfirmed = true">
							Proceed
						</button>
						<button type="button" v-if="customCampaign.dataConfirmed && customCampaign.name !== ''" class="btn btn-primary float-end" @click="createCampaign()">
							Create campaign
						</button>
					</div>
				</div>
			</div>
		</div>

		<!-- Modal for displaying Data -->
		<div class="modal fade" id="dataInfoModal" tabindex="-1" aria-labelledby="createLabel" aria-hidden="true">
			<div class="modal-dialog modal-lg modal-dialog-centered">
				<div class="modal-content">
					<div class="modal-body">
						<div>
							<div class="row border-bottom mb-2 pb-1">
								<div class="col-1">
									<h1>📬</h1>
								</div>
								<div class="col-6">
									<h4 class="mb-0 d-flex">
										{{ dataInfo.name }}
										<span class="badge bg-warning badge-warning text-white ms-2" v-if="!dataInfo.live">test</span>
									</h4>
									<span>{{ dataInfo.type }}</span>
								</div>
								<div class="col-5">
									<span v-if="dataInfo.status === 'preparing'" class="bg-info p-2 rounded text-white fs-5 float-end text-capitalize">
										<font-awesome-icon :icon="['fas', 'info-circle']" /> {{ dataInfo.status }}
									</span>
									<span v-if="dataInfo.status === 'canceled'" class="bg-warning p-2 rounded text-white fs-5 float-end text-capitalize">
										<font-awesome-icon :icon="['fas', 'times']" /> {{ dataInfo.status }}
									</span>
									<span v-if="dataInfo.status === 'sent'" class="bg-success p-2 rounded text-white fs-5 float-end text-capitalize">
										<font-awesome-icon :icon="['fas', 'check-circle']" />
										{{ dataInfo.status }}</span
									>
									<span v-if="dataInfo.status === 'scheduled'" class="bg-primary p-2 rounded text-white fs-5 float-end text-capitalize">
										<font-awesome-icon :icon="['fas', 'clock']" /> {{ dataInfo.status }} for
										{{ dataInfo.send_at }}
									</span>
								</div>
							</div>
						</div>
						<div>
							<div v-if="dataInfo.info" class="alert alert-primary mb-2" role="alert">Campaign deliveries info: {{ dataInfo.info }}</div>
							<div class="info-description fw-bold mb-2">
								{{ dataInfo.description }}
							</div>
							<div class="info-filters mb-2">
								<p>
									Applied filters are:
									<span :key="filter" v-for="filter in dataInfo.printingFilters" class="badge badge-primary bg-primary me-1">{{ filter }}</span>
								</p>
							</div>
							<div class="info-details mb-2">
								<div class="info-details-item">
									<div class="mb-2">
										Sender: <span class="fw-bold">{{ dataInfo.sender }}</span>
									</div>
									<div class="mb-2">
										Municipalities: <span class="fw-bold">{{ dataInfo.municipalities_count }}</span>
									</div>
									<div class="mb-2" v-if="dataInfo.postcard_size">
										Postcard size: <span class="fw-bold">{{ dataInfo.postcard_size }}</span>
									</div>
									<div class="mb-2" v-if="dataInfo.letter_color">
										Letter color: <span class="fw-bold">{{ dataInfo.letter_color }}</span>
									</div>
									<div class="mb-2" v-if="dataInfo.double_sided">
										Letter double sided: <span class="fw-bold">{{ dataInfo.double_sided }}</span>
									</div>
									<div class="mb-2" v-if="dataInfo.letter_address_placement">
										Letter address placement:
										<span class="fw-bold">{{ dataInfo.letter_address_placement }}</span>
									</div>
									<div class="mb-2" v-if="dataInfo.letter_extra_service">
										Letter extra service:
										<span class="fw-bold">{{ dataInfo.letter_extra_service }}</span>
									</div>
									<div class="mb-2" v-if="dataInfo.send_at">
										Send time:
										<span class="fw-bold">{{ dataInfo.send_at }}</span>
									</div>
								</div>
							</div>
							<div class="info-letter-pdf mb-2" v-if="dataInfo.letter_pdf">
								Letter PDF:
								<a target="_blank" :href="storageUrl + dataInfo.letter_pdf"> preview.pdf 🔎</a>
							</div>
							<div class="info-postcard-preview row " v-if="dataInfo.postcard_front && dataInfo.postcard_back">
								<div class="col-6">
									Postcard front
									<img v-if="dataInfo.postcard_front && !uploadLoading" :src="storageUrl + dataInfo.postcard_front" class="w-100" />
								</div>
								<div class="col-6">
									Postcard back
									<img v-if="dataInfo.postcard_back && !uploadLoading" :src="storageUrl + dataInfo.postcard_back" class="w-100" />
								</div>
							</div>
							<pre class="d-none">{{ dataInfo }}</pre>
						</div>
					</div>
					<div class="modal-footer">
						<button type="button" @click="moreDetails()" class="btn btn-info">More details</button>
						<button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
					</div>
				</div>
			</div>
		</div>

		<!-- Modal for adding Filter -->
		<div class="modal fade" id="addFilter" tabindex="-1" aria-labelledby="addFilterLabel" aria-hidden="true">
			<div class="modal-dialog">
				<div class="modal-content">
					<form @submit.prevent="addFilter">
						<div class="modal-header">
							<h5 class="modal-title" id="exampleModalLabel">Add Filter</h5>
							<small class="ms-2">Adding new filter for {{ this.model }}</small>
							<button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
						</div>
						<div class="modal-body text-center">
							<h1>Will come soon 😉</h1>
						</div>
						<div class="modal-footer">
							<button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
							<!-- <button type="submit" class="btn btn-primary">Save</button> -->
						</div>
					</form>
				</div>
			</div>
		</div>

		<!-- Modal for export -->
		<div class="modal fade" id="exportModal" tabindex="-1" aria-labelledby="exportModalLabel" aria-hidden="true">
			<div class="modal-dialog">
				<div class="modal-content">
					<div class="modal-header">
						<h5 class="modal-title" id="exampleModalLabel">Export {{ route }}</h5>
						<button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
					</div>
					<div class="modal-body">
						<div class="form-group mb-2">
							<label for="">Export Type</label>
							<select class="form-select" v-model="newExport.type">
								<option value="csv">CSV</option>
							</select>
						</div>
						<div class="form-group mb-2">
							<label for="">File Name</label>
							<div class="input-group mb-3">
								<input type="text" class="form-control" placeholder="File name" v-model="newExport.filename" />
								<span class="input-group-text" id="basic-addon2">.{{ newExport.type }}</span>
							</div>
						</div>
						<div class="form-group">
							<label for="">Columns to export</label>
							<div class="form-check">
								<input class="form-check-input" type="radio" v-model="newExport.columnsType" value="visible" id="visibleColumnsRadio" checked />
								<label class="form-check-label" for="visibleColumnsRadio">
									Export visible columns
								</label>
							</div>
							<div class="my-2" v-if="newExport.columnsType === 'visible'">
								<div class="d-flex flex-wrap">
									<div :key="'column-available-' + column" v-for="column in newExport.exportColumns" class="mb-2 me-1">
										<span class="badge bg-success">{{ column }}</span>
									</div>
								</div>
							</div>
							<div class="form-check">
								<input class="form-check-input" type="radio" v-model="newExport.columnsType" value="custom" id="customColumnsRadio" />
								<label class="form-check-label" for="customColumnsRadio">
									Export custom columns
								</label>
							</div>
							<div class="my-2" v-if="newExport.columnsType === 'custom'">
								<div class="d-flex flex-wrap">
									<small class="w-100 mb-1">ℹ️ Click on column to include/exclude it from export</small>
									<div :key="'column-available-' + column" v-for="column in newExport.availableColumns" class="mb-2 me-1">
										<span role="button" @click="toggleExportColumn(column)" v-if="newExport.exportColumns.includes(column)" class="badge bg-success">{{
											column
										}}</span>
										<span role="button" @click="toggleExportColumn(column)" v-else class="badge bg-secondary">{{ column }}</span>
									</div>
								</div>
							</div>
						</div>
					</div>
					<div class="modal-footer">
						<button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
						<button v-if="!loadExporting && exportingFile === ''" @click="exportData()" type="submit" class="btn btn-primary">
							Create Export
						</button>
						<a v-if="!loadExporting && exportingFile !== ''" @click="downloadExport()" :href="exportingFile" class="btn btn-primary" download>
							Download
						</a>

						<button v-if="loadExporting" class="btn btn-primary" type="button" disabled>
							<span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
							Loading...
						</button>
					</div>
				</div>
			</div>
		</div>

		<!-- Offcanvas for Filters -->
		<div class="offcanvas offcanvas-end" tabindex="-1" id="offcanvasFilters" aria-labelledby="offcanvasRightLabel">
			<div class="offcanvas-header">
				<h5 id="offcanvasRightLabel">Filters settings</h5>
				<button type="button" class="btn-close text-reset" data-bs-dismiss="offcanvas" aria-label="Close"></button>
			</div>
			<div class="offcanvas-body">
				Filters settings
			</div>
		</div>

		<!-- Offcanvas for Searches -->
		<div class="offcanvas offcanvas-end" tabindex="-1" id="offcanvasSearches" aria-labelledby="offcanvasRightLabel">
			<div class="offcanvas-header">
				<h5 id="offcanvasRightLabel">Saved searches</h5>
				<button type="button" class="btn-close text-reset" data-bs-dismiss="offcanvas" aria-label="Close"></button>
			</div>
			<div class="offcanvas-body">
				<h4 class="m-0">Your saved searches</h4>
				<p class="p-0"><small>ℹ️ Click on search to apply search filters to table</small></p>
				<div class="table-columns-settings">
					<div class="d-flex flex-wrap">
						<div :key="'user-searches-' + search.id" v-for="search in searches" class="mb-2 me-1">
							<span
								data-bs-toggle="offcanvas"
								data-bs-target="#offcanvasSearches"
								aria-controls="offcanvasSearches"
								role="button"
								@click="applySearch(search)"
								class="badge bg-success"
								>{{ search.name }}</span
							>
						</div>
					</div>
				</div>

				<small>👨‍🏫 Your saved searches for fast applying filters, you can add important ones to display above table (🚧 coming soon)</small>
			</div>
		</div>
		<!-- Offcanvas for Table Settings -->
		<div class="offcanvas offcanvas-end" tabindex="-1" id="offcanvasTableSettings" aria-labelledby="offcanvasRightLabel">
			<div class="offcanvas-header">
				<h5 id="offcanvasRightLabel">Table settings</h5>
				<button type="button" class="btn-close text-reset" data-bs-dismiss="offcanvas" aria-label="Close"></button>
			</div>
			<div class="offcanvas-body">
				<h4 class="m-0">Display columns</h4>
				<p class="p-0"><small>ℹ️ Select which columns to display in table</small></p>
				<div class="table-columns-settings">
					<div class="d-flex flex-wrap">
						<div :key="'column-available-' + column" v-for="column in availableColumns" class="mb-2 me-1">
							<span role="button" @click="toggleUserColumn(column)" v-if="userColumns.includes(column)" class="badge bg-success">{{ column }}</span>
							<span role="button" @click="toggleUserColumn(column)" v-else class="badge bg-secondary">{{ column }}</span>
						</div>
					</div>
				</div>

				<small>👨‍🏫 Selected columns are automatically saved to your browser storage and will be used again when you come on this page</small>
				<h4 class="mt-3">Display {{ this.route }} per page</h4>
				<p class="p-0">
					<small>ℹ️ Select how many {{ this.route }} to display per page in table</small>
				</p>
				<div class="form-group w-75">
					<select v-model="userPerPage" @change="setUserPerPage()" class="form-select">
						<option value="10">10 per page</option>
						<option value="15">15 per page</option>
						<option value="20">20 per page</option>
						<option value="30">30 per page</option>
						<option value="50">50 per page</option>
						<option value="100">100 per page</option>
					</select>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import PageHeader from '@/components/layout/PageHeader'
import UtmCampaignInput from '@/components/common/UtmCampaignInput'
import UtmContentInput from '@/components/common/UtmContentInput'
import { mapState } from 'vuex'
import restApi from '../../api/index.js'
import { Modal } from 'bootstrap'
import { handleErrors } from '../../actions/helpers'
import LPDataTablePagination from '@/components/lp-data-table/LPDataTablePagination'
import LPDataTableFilters from '@/components/lp-data-table/LPDataTableFilters'
import LPDataTableThead from '@/components/lp-data-table/LPDataTableThead'
import LPDataTableTbody from '@/components/lp-data-table/LPDataTableTbody'
import MunicipalityAdvancedSearch from '@/components/municipality/MunicipalityAdvancedSearch'

export default {
	name: 'Campaigns',
	components: {
		UtmCampaignInput, UtmContentInput,
		PageHeader,
		LPDataTablePagination,
		LPDataTableFilters,
		LPDataTableThead,
		LPDataTableTbody,
		MunicipalityAdvancedSearch,
	},
	data() {
		return {
			route: 'campaigns',
			model: 'campaign',
			tableData: [],
			loading: false,
			error: '',
			newTableEntry: {},
			$createModal: null,
			$exportModal: null,
			$addFilterModal: null,
			pagination: {},
			page: this.$route.query.page || 1,
			orderBy: this.$route.query.orderBy || 'created_at',
			direction: this.$route.query.direction || 'desc',
			userPerPage: localStorage.getItem('campaign-per-page') || 20,
			table: {
				columns: [
					{
						name: 'id',
						label: 'ID',
						sort: true,
						type: 'default',
					},
					{
						name: 'name',
						label: 'Name',
						sort: true,
						type: 'custom',
						style: 'code1 <span class="badge  ms-2 p-1 px-2 code3">code2</badge>',
						values: [
							{
								name: 'code1',
								type: 'data',
								value: 'name',
							},
							{
								name: 'code2',
								type: 'conditions',
								value: 'live',
								conditions: [
									{
										value: 1,
										replace: 'Live',
									},
									{
										value: 0,
										replace: 'Test',
									},
								],
							},
							{
								name: 'code3',
								type: 'class',
								value: 'live',
								conditions: [
									{
										value: 1,
										replace: 'd-none',
									},
									{
										value: 0,
										replace: 'text-warning',
									},
								],
							},
						],
					},
					{
						name: 'description',
						label: 'Description',
						sort: false,
						type: 'default',
					},
					{
						name: 'images',
						label: 'images',
						sort: false,
						type: 'default',
					},
					{
						name: 'status',
						label: 'Status',
						sort: true,
						type: 'campaignStatus',
						/* style: ' <span class="badge badge-primary ms-2 code2">code1</badge>',
						values: [
							{
								name: 'code1',
								type: 'data',
								value: 'status',
							},

							{
								name: 'code2',
								type: 'class',
								value: 'status',
								conditions: [
									{
										value: 'sent',
										replace: 'bg-success',
									},
									{
										value: 'scheduled',
										replace: 'bg-info',
									},
									{
										value: 'canceled',
										replace: 'bg-warning',
									},
									{
										value: 'preparing',
										replace: 'bg-primary',
									},
								],
							},
						], */
					},
					{
						name: 'live',
						label: 'Live',
						sort: true,
						type: 'boolean',
						yes: 'yes',
						no: 'no',
					},
					{
						name: 'type',
						label: 'Type',
						sort: true,
						type: 'default',
					},
					{
						name: 'postcard_size',
						label: 'Postcard Size',
						sort: false,
						type: 'default',
					},
					{
						name: 'letter_color',
						label: 'Letter color',
						sort: true,
						type: 'boolean',
						yes: 'yes',
						no: 'no',
					},
					{
						name: 'letter_double_sided',
						label: 'Letter double sided',
						sort: true,
						type: 'boolean',
						yes: 'yes',
						no: 'no',
					},
					{
						name: 'letter_address_placement',
						label: 'Letter address placement',
						sort: true,
						type: 'default',
					},
					{
						name: 'letter_extra_service',
						label: 'Letter extra_service',
						sort: true,
						type: 'default',
					},
					{
						name: 'mail_type',
						label: 'Mail type',
						sort: true,
						type: 'default',
					},
					{
						name: 'thumbnail',
						label: 'Thumbnail',
						sort: true,
						type: 'default',
					},
					{
						name: 'municipalities_count',
						label: 'Count',
						sort: true,
						type: 'default',
					},
					{
						name: 'filters',
						label: 'Filters',
						sort: false,
						type: 'default',
					},
					{
						name: 'send_at',
						label: 'Send Date',
						sort: true,
						type: 'default',
					},
					{
						name: 'created_at',
						label: 'Created',
						sort: true,
						type: 'default',
					},
					{
						name: 'updated_at',
						label: 'Updated',
						sort: false,
						type: 'default',
					},
					{
						name: 'actions',
						label: 'Actions',
						sort: false,
						type: 'actions',
						width: 150,
						buttons: [
							{
								type: 'default',
								label: 'View',
								action: '',
								class: 'btn-secondary',
								permission: `CAMPAIGN_ACCESS`,
							},
							{
								type: 'default',
								label: 'Edit',
								action: '',
								class: 'btn-primary',
								permission: `CAMPAIGN_UPDATE`,
							},
							{
								type: 'delete',
								label: 'Delete',
								action: '',
								class: 'btn-danger',
								permission: `CAMPAIGN_DELETE`,
							},
						],
					},
				],
				filters: [
					{
						display: true,
						name: 'search',
						label: 'Search',
						type: 'text',
						placeholder: 'enter text and press enter',
						colClass: 'col-3',
						value: this.$route.query.search || '',
					},
					{
						display: true,
						name: 'live',
						label: 'Live',
						type: 'boolean',
						placeholder: 'Select any',
						colClass: 'col-2',
						value: this.$route.query.live || '',
						options: [
							{
								id: '0',
								name: 'Test',
							},
							{
								id: '1',
								name: 'Live',
							},
						],
					},
					{
						display: true,
						name: 'type',
						label: 'Type',
						type: 'boolean',
						placeholder: 'Select any',
						colClass: 'col-2',
						value: this.$route.query.type || '',
						options: [
							{
								id: 'postcard',
								name: 'postcard',
							},
							{
								id: 'letter',
								name: 'letter',
							},
						],
					},
					{
						display: true,
						name: 'created_at',
						label: 'Created',
						type: 'daterange',
						placeholder: '',
						colClass: 'col-4',
						from: this.$route.query.fromcreated_at || '',
						to: this.$route.query.tocreated_at || '',
					},
				],
			},
			params: {},
			userColumns: JSON.parse(localStorage.getItem(`campaign-columns`)) || ['name', 'type', 'live', 'send_at', 'actions'],
			availableColumns: ['id'],
			searchName: '',
			searchSaving: false,

			searchActive: '',
			searches: [],
			newExport: {
				type: 'csv',
				columnsType: 'visible',
				filename: '',
				availableColumns: ['id'],
				exportColumns: JSON.parse(localStorage.getItem(`campaign-columns`)) || ['id', 'is_letter', 'description', 'actions'],
			},
			loadExporting: false,
			exportingFile: '',
			syncingPostcards: false,
			syncingLetters: false,
			$dataInfoModal: null,
			dataInfo: {},
			$campaignModal: null,
			customCampaign: {
				name: '',
				description: '',
				utm_campaign: '',
				utm_content: '',
				type: 'manual',
				typeConfirmed: false,
				municipalities: [],
				municipalitiesIds: [],
				dataConfirmed: false,
				importFile: null,
				importLoading: false,
				importSummary: null,
				importError: '',
			},
		}
	},
	computed: {
		...mapState(['authUser', 'storageUrl']),
	},
	created() {
		this.table.filters.forEach(filter => {
			if (filter.value && filter.value !== '' && filter.value.length > 0) {
				this.params[filter.name] = filter.value
			} else {
				delete this.params[filter.name]
			}

			if (filter.from && filter.from !== '' && filter.to && filter.to !== '') {
				this.params['from' + filter.name] = filter.from
				this.params['to' + filter.name] = filter.to
			} else {
				delete this.params['from' + filter.name]
				delete this.params['to' + filter.name]
			}
		})
		this.getTableColumns()
		this.getTableData(true)
		this.getSearches()
	},
	mounted() {
		this.$createModal = new Modal('#create')
		this.$addFilterModal = new Modal('#addFilter')
		this.$exportModal = new Modal('#exportModal')
		this.$dataInfoModal = new Modal('#dataInfoModal')
		this.$campaignModal = new Modal('#campaignModal')
	},
	methods: {
		// Get table Columns
		getTableColumns() {
			restApi
				.post('/helper/table-columns', { table: this.route })
				.then(({ data }) => {
					this.newExport.availableColumns = data.map(column => column.name)
					this.availableColumns = data.map(column => column.name)
					this.availableColumns.push('actions')
				})
				.catch(error => {
					this.$swal.fire({
						icon: 'error',
						title: handleErrors(error, ''),
						timer: 6000,
					})
				})
		},
		// Get table data from API
		getTableData(first = false) {
			this.loading = true

			this.params = {
				...this.params,
				page: this.page,
				orderBy: this.orderBy,
				direction: this.direction,
				perPage: localStorage.getItem(`${this.model}-per-page`) || 20,
			}

			restApi
				.get(`/${this.route}`, { params: this.params })
				.then(({ data }) => {
					this.tableData = data.data
					this.pagination = data
					delete this.pagination.data
					this.loading = false
					//this.$router.replace({ query: this.params })
					if (!first) {
						this.$router.push({ path: this.$router.currentPath, query: this.params })
					}
				})
				.catch(error => {
					this.$swal.fire({
						icon: 'error',
						title: handleErrors(error, ''),
						timer: 6000,
					})
				})
		},

		// Adding new filter
		addFilter() {
			this.$addFilterModal.hide()
		},

		// Go to user page
		viewUser(user) {
			this.$router.push('/users/' + user.id)
		},

		// Get items from page that returned from LPDataTablePagination
		getItems(page) {
			this.page = page
			this.getTableData()
		},

		// Set direction
		setOrderBy(orderBy, direction) {
			this.orderBy = orderBy
			this.direction = direction
			this.getTableData()
		},

		// Get filters from LPDataTableFilters component
		getFilters(filters) {
			this.page = ''
			this.searchActive = ''
			filters.forEach(filter => {
				if (filter.value && filter.value !== '' && filter.value.length > 0) {
					this.params[filter.name] = filter.value
				} else {
					delete this.params[filter.name]
				}

				// Setting filter if its type is range
				if (filter.from && filter.from !== '' && filter.to && filter.to !== '') {
					this.params['from' + filter.name] = filter.from
					this.params['to' + filter.name] = filter.to
				} else {
					delete this.params['from' + filter.name]
					delete this.params['to' + filter.name]
				}
			})
			this.getTableData()
		},

		// Toggle user column
		toggleUserColumn(column) {
			if (this.userColumns.includes(column)) {
				this.userColumns = this.userColumns.filter(c => c !== column)
			} else {
				this.userColumns.push(column)
			}
			this.updateUserColumns()
		},

		// Update user columns
		updateUserColumns() {
			localStorage.setItem(`${this.model}-columns`, JSON.stringify(this.userColumns))
		},

		// Get user saved searches
		getSearches() {
			let params = {
				user_id: this.authUser.id,
				model: this.model,
			}
			restApi
				.get('/searches', { params })
				.then(({ data }) => {
					data.forEach(d => {
						d.data = JSON.parse(d.data)
						return d
					})
					this.searches = data
				})
				.catch(error => {
					this.$swal.fire({
						icon: 'error',
						title: handleErrors(error, ''),
						timer: 6000,
					})
				})
		},

		// Enable saving if some filters are set
		haveParamsToSave() {
			if (Object.values(this.params).length > 4) {
				return true
			} else {
				return false
			}
		},

		// Get user saved searches
		storeSearch() {
			let dataParams = this.params
			restApi
				.post('/searches', {
					user_id: this.authUser.id,
					model: this.model,
					name: this.searchName,
					data: JSON.stringify(dataParams),
				})
				.then(({ data }) => {
					data.data = JSON.parse(data.data)
					this.searches.push(data)
					this.searchActive = this.searchName
					this.searchSaving = false
					this.$swal.fire({
						icon: 'success',
						title: 'Search successfully saved!',
						timer: 4000,
					})
				})
				.catch(error => {
					this.$swal.fire({
						icon: 'error',
						title: handleErrors(error, ''),
						timer: 6000,
					})
				})
		},

		// Apply saved search to table
		applySearch(search) {
			// Reset all before applying saved search
			this.params = {}
			this.table.filters.forEach(filter => {
				if (filter.type !== 'range' && filter.type !== 'daterange') {
					filter.value = ''
				} else {
					filter.from = ''
					filter.to = ''
				}
			})
			// Apply saved search
			this.params = search.data
			let paramsArray = Object.keys(search.data)
			this.table.filters.forEach(filter => {
				if (paramsArray.includes(filter.name) && filter.type !== 'range') {
					filter.value = this.params[filter.name]
				}
				if (paramsArray.includes('from' + filter.name) && filter.type === 'range') {
					filter.from = this.params['from' + filter.name]
					filter.to = this.params['to' + filter.name]
				}
				if (paramsArray.includes('from' + filter.name) && filter.type === 'daterange') {
					filter.from = this.params['from' + filter.name]
					filter.to = this.params['to' + filter.name]
				}
			})
			this.searchActive = search.name
			this.getTableData()
		},

		// Clear all filters
		clearFilters() {
			this.params = {}
			this.searchActive = ''
			this.table.filters.forEach(filter => {
				if (filter.type !== 'range' && filter.type !== 'multiselect') {
					filter.value = ''
				}
				if (filter.type === 'multiselect') {
					filter.value = []
				}
				if (filter.type === 'range' || filter.type === 'daterange') {
					filter.from = ''
					filter.to = ''
				}
			})
			this.getTableData()
		},

		// Toggle export column
		toggleExportColumn(column) {
			if (this.newExport.exportColumns.includes(column)) {
				this.newExport.exportColumns = this.newExport.exportColumns.filter(c => c !== column)
			} else {
				this.newExport.exportColumns.push(column)
			}
		},

		// Export table Data
		exportData() {
			this.loadExporting = true
			this.newExport.exportColumns = this.newExport.exportColumns.filter(column => column !== 'actions')
			restApi
				.post(`/${this.route}/export`, {
					params: JSON.stringify(this.params),
					columns: JSON.stringify(this.newExport.exportColumns),
					filename: this.newExport.filename,
				})
				.then(({ data }) => {
					this.loadExporting = false
					this.exportingFile = this.storageUrl + '/tmp/' + data.file
				})
				.catch(error => {
					this.$swal.fire({
						icon: 'error',
						title: handleErrors(error, ''),
						timer: 6000,
					})
				})
		},

		// Download exported Data
		downloadExport() {
			this.$exportModal.hide()
			this.newExport.filename = ''
			this.exportingFile = ''
		},

		// Set user per page
		setUserPerPage() {
			localStorage.setItem(`${this.model}-per-page`, this.userPerPage)
			this.getTableData()
		},

		// Sync postcards from LOB
		syncPostcards() {
			this.syncingPostcards = true
			restApi
				.get(`/${this.route}/syncPostcards`)
				.then(() => {
					this.getTableData()
					this.syncingPostcards = false
				})
				.catch(error => {
					this.syncingPostcards = false
					this.$swal.fire({
						icon: 'error',
						title: handleErrors(error, ''),
						timer: 6000,
					})
				})
		},

		// Sync letters from LOB
		syncLetters() {
			this.syncingLetters = true
			restApi
				.get(`/${this.route}/syncLetters`)
				.then(() => {
					this.getTableData()
					this.syncingLetters = false
				})
				.catch(error => {
					this.syncingLetters = false
					this.$swal.fire({
						icon: 'error',
						title: handleErrors(error, ''),
						timer: 6000,
					})
				})
		},

		showInformationModal(event) {
			restApi
				.get(`/${event.route}/${event.id}`)
				.then(({ data }) => {
					this.dataInfo = data
					this.dataInfo.filters = JSON.parse(data.filters)
					this.dataInfo.printingFilters = []
					this.$dataInfoModal.show()
					for (const property in data.filters) {
						if (property !== 'page' && property !== 'orderBy' && property !== 'direction' && property !== 'perPage') {
							let value = data.filters[property]
							if (value) {
								value === 'Yes'
							}
							if (!value) {
								value === 'No'
							}
							this.dataInfo.printingFilters.push(`${property}: ${value}`)
						}
					}
				})
				.catch(error => {
					this.$swal.fire({
						icon: 'error',
						title: handleErrors(error, ''),
						timer: 6000,
					})
					this.loading = false
				})
		},

		moreDetails() {
			this.$dataInfoModal.hide()
			this.$router.push('/campaigns/edit/' + this.dataInfo.id)
		},

		// Add municipalities from MunicipalityAdvancedSearch to array
		addMunicipalityToCampaign(municipality) {
			if (!this.customCampaign.municipalities.includes(municipality.id)) {
				this.customCampaign.municipalities.push(municipality)
				this.customCampaign.municipalitiesIds.push(municipality.id)
			}
		},

		// Remove municipality
		removeMunicipalityFromCampaign(id) {
			this.customCampaign.municipalities = this.customCampaign.municipalities.filter(m => m.id !== id)
			this.customCampaign.municipalitiesIds = this.customCampaign.municipalitiesIds.filter(m => m.id !== id)
		},

		onImportFileChange(event) {
			const file = event.target.files && event.target.files.length > 0 ? event.target.files[0] : null
			this.customCampaign.importFile = file
			this.customCampaign.importError = ''
			this.customCampaign.importSummary = null
		},

		uploadMunicipalitiesCsv() {
			if (!this.customCampaign.importFile) {
				this.customCampaign.importError = 'Please select a CSV file first.'
				return
			}

			this.customCampaign.importLoading = true
			this.customCampaign.importError = ''
			this.customCampaign.importSummary = null

			const formData = new FormData()
			formData.append('file', this.customCampaign.importFile)

			restApi
				.post('/campaigns/import-municipalities', formData, {
					headers: { 'Content-Type': 'multipart/form-data' },
				})
				.then(({ data }) => {
					this.customCampaign.municipalities = data.municipalities || []
					this.customCampaign.municipalitiesIds = this.customCampaign.municipalities.map(m => m.id)
					this.customCampaign.importSummary = data.summary || null
				})
				.catch(error => {
					this.customCampaign.municipalities = []
					this.customCampaign.municipalitiesIds = []
					this.customCampaign.importError = handleErrors(error, 'Failed to import municipalities')
				})
				.finally(() => {
					this.customCampaign.importLoading = false
				})
		},

		// Create campaign
		createCampaign() {
			let nCampaign = {
				name: this.customCampaign.name,
				description: this.customCampaign.description,
				utm_campaign: this.customCampaign.utm_campaign,
				utm_content: this.customCampaign.utm_content,
				municipalities_count: this.customCampaign.municipalitiesIds.length,
				municipalities: this.customCampaign.municipalitiesIds,
				data_type: 'custom',
				objects_type: 'municipality',
				campaignObjects: this.customCampaign.municipalitiesIds,
			}
			// Store new campaign
			restApi
				.post(`/campaigns`, nCampaign)
				.then(({ data }) => {
					this.$campaignModal.hide()
					this.$router.push(`/campaigns/edit/${data.id}`)
				})
				.catch(error => {
					this.$swal.fire({
						icon: 'error',
						title: handleErrors(error, ''),
						timer: 6000,
					})
				})
		},
	},
}
</script>
